import * as emailjs from "@emailjs/browser";
import { EmailTemplateParams, QuoteEmailData, EmailResponse } from "./types";
import { emailConfigs, validateEmailConfig } from "./config";

/**
 * Email Service for handling quote requests and other email communications
 * Uses EmailJS for sending emails from the frontend
 */
class EmailService {
  private isQuoteConfigValid: boolean;
  private isContactConfigValid: boolean;

  constructor() {
    this.isQuoteConfigValid = validateEmailConfig("quote");
    this.isContactConfigValid = validateEmailConfig("contact");
  }

  /**
   * Sends a quote request email
   * @param quoteData - Customer quote request data
   * @returns Promise<EmailResponse>
   */
  async sendQuoteRequest(quoteData: QuoteEmailData): Promise<EmailResponse> {
    if (!this.isQuoteConfigValid) {
      return {
        success: false,
        message: "Quote email service is not properly configured",
      };
    }

    try {
      const templateParams = this.prepareQuoteEmailTemplate(quoteData);
      const config = emailConfigs.quote;

      const response = await emailjs.send(
        config.serviceId,
        config.templateId,
        templateParams,
        config.publicKey
      );

      if (response.status === 200) {
        return {
          success: true,
          status: response.status,
          message: "Email sent successfully",
        };
      } else {
        return {
          success: false,
          status: response.status,
          message: "Failed to send email",
        };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error sending quote request email:", error);
      return {
        success: false,
        error,
        message: "An error occurred while sending the email",
      };
    }
  }

  /**
   * Prepares email template parameters for quote requests
   * @param quoteData - Customer quote request data
   * @returns EmailTemplateParams
   */
  private prepareQuoteEmailTemplate(
    quoteData: QuoteEmailData
  ): EmailTemplateParams {
    const config = emailConfigs.quote;
    const formattedMessage = `
New Quote Request from Maa Saryu Agency Website

Customer Details:
- Name: ${quoteData.name}
- Email: ${quoteData.email}
- Phone: ${quoteData.phone}
- Company: ${quoteData.company || "Not provided"}

Project Description:
${quoteData.projectDescription}

Please contact the customer to discuss their requirements and provide a quote.
    `.trim();

    return {
      to_email: config.recipientEmail,
      from_name: quoteData.name,
      from_email: quoteData.email,
      phone: quoteData.phone,
      company: quoteData.company || "Not provided",
      project_description: quoteData.projectDescription,
      message: formattedMessage,
      reply_to: quoteData.email,
    };
  }

  /**
   * Sends a general contact email
   * @param contactData - Contact form data
   * @returns Promise<EmailResponse>
   */
  async sendContactEmail(contactData: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<EmailResponse> {
    if (!this.isContactConfigValid) {
      return {
        success: false,
        message: "Contact email service is not properly configured",
      };
    }

    try {
      const formattedMessage = `
New Contact Message from Maa Saryu Agency Website

Customer Details:
- Name: ${contactData.name}
- Email: ${contactData.email}
- Subject: ${contactData.subject}

Message:
${contactData.message}

Please respond to the customer inquiry.
      `.trim();

      const config = emailConfigs.contact;
      const templateParams = {
        to_email: config.recipientEmail,
        from_name: contactData.name,
        from_email: contactData.email,
        phone: "Not provided",
        company: "Not provided",
        project_description: `Subject: ${contactData.subject}\n\n${contactData.message}`,
        message: formattedMessage,
        reply_to: contactData.email,
      };

      const response = await emailjs.send(
        config.serviceId,
        config.templateId,
        templateParams,
        config.publicKey
      );

      if (response.status === 200) {
        return {
          success: true,
          status: response.status,
          message: "Email sent successfully",
        };
      } else {
        return {
          success: false,
          status: response.status,
          message: "Failed to send email",
        };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error sending contact email:", error);
      return {
        success: false,
        error,
        message: "An error occurred while sending the email",
      };
    }
  }

  /**
   * Gets the current email configuration status
   * @returns object with quote and contact config status
   */
  isConfigurationValid(): {
    quote: boolean;
    contact: boolean;
    overall: boolean;
  } {
    return {
      quote: this.isQuoteConfigValid,
      contact: this.isContactConfigValid,
      overall: this.isQuoteConfigValid && this.isContactConfigValid,
    };
  }
}

// Export a singleton instance
export const emailService = new EmailService();
export default emailService;
